import React, { useState } from 'react';
import { X, ChevronRight, ChevronLeft, Settings, Database, FileSpreadsheet, Play, CheckCircle } from 'lucide-react';

interface TutorialGuideProps {
  isOpen: boolean;
  onClose: () => void;
}

const STEPS = [
  {
    title: "欢迎使用 GenStudio Pro",
    desc: "这是一个专业的批量图像生成与参考图管理工作台。通过简单的几步，您可以利用 Gemini 强大的多模态能力进行批量创作。",
    icon: <div className="text-4xl">👋</div>
  },
  {
    title: "第一步：配置 API",
    desc: "在开始之前，请点击右上角的设置图标（Settings），输入您的 Google Gemini 或第三方 API Key。这是生成图片的基础。",
    icon: <Settings size={48} className="text-indigo-400" />
  },
  {
    title: "第二步：管理参考图库",
    desc: "左侧边栏是您的素材库。您可以上传角色、场景或风格参考图。系统会自动根据提示词中的关键词（如 'cat'）匹配对应的参考图。",
    icon: <Database size={48} className="text-emerald-400" />
  },
  {
    title: "第三步：导入提示词",
    desc: "点击顶部的“导入 CSV”按钮。文件需包含两列：'index'（序号）和 'prompt'（提示词）。系统会自动为每个提示词创建任务卡片。",
    icon: <FileSpreadsheet size={48} className="text-orange-400" />
  },
  {
    title: "第四步：批量生成",
    desc: "准备好后，点击“批量生成”按钮。AI 将根据您的提示词和自动匹配的参考图，一次性生成多组图片。您可以随时编辑或重新生成。",
    icon: <Play size={48} className="text-blue-400" />
  }
];

export const TutorialGuide: React.FC<TutorialGuideProps> = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);

  if (!isOpen) return null;

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      onClose();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 transition-all duration-300">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden flex flex-col relative">
        
        {/* Close Button */}
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-500 hover:text-white transition-colors z-10"
        >
          <X size={20} />
        </button>

        {/* Content Area */}
        <div className="p-8 flex flex-col items-center text-center space-y-6 pt-12">
          <div className="w-20 h-20 bg-slate-800 rounded-full flex items-center justify-center shadow-inner border border-slate-700 mb-2">
            {STEPS[currentStep].icon}
          </div>
          
          <div className="space-y-3">
            <h2 className="text-2xl font-bold text-white tracking-tight">
              {STEPS[currentStep].title}
            </h2>
            <p className="text-slate-400 text-sm leading-relaxed">
              {STEPS[currentStep].desc}
            </p>
          </div>
        </div>

        {/* Progress Dots */}
        <div className="flex justify-center gap-2 mb-2">
          {STEPS.map((_, idx) => (
            <div 
              key={idx} 
              className={`w-2 h-2 rounded-full transition-all duration-300 ${idx === currentStep ? 'bg-indigo-500 w-6' : 'bg-slate-700'}`}
            />
          ))}
        </div>

        {/* Footer / Controls */}
        <div className="p-6 border-t border-slate-800 bg-slate-900/50 flex justify-between items-center">
          <button 
            onClick={handlePrev}
            disabled={currentStep === 0}
            className={`flex items-center gap-1 text-sm font-medium px-3 py-2 rounded-lg transition-colors ${currentStep === 0 ? 'text-slate-700 cursor-not-allowed' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}
          >
            <ChevronLeft size={16} /> 上一步
          </button>

          <button 
            onClick={handleNext}
            className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white px-6 py-2 rounded-lg text-sm font-bold shadow-lg shadow-indigo-900/20 transition-all active:scale-95"
          >
            {currentStep === STEPS.length - 1 ? (
              <>开始使用 <CheckCircle size={16} /></>
            ) : (
              <>下一步 <ChevronRight size={16} /></>
            )}
          </button>
        </div>

      </div>
    </div>
  );
};
